/**
 * Adding a client. Shared by the clients page and the client picker in sales
 * documents.
 *
 * The name field autocompletes against the business register and prefills the
 * registry code, VAT number, email, phone, website and address from the real
 * record, so the common case is: type three letters, pick the company, save.
 * Everything below the name is there for the case the register cannot answer -
 * a private person, a foreign company, a client who is not registered yet.
 */
import { useState } from "react";
import { Loader2 } from "lucide-react";
import { AddressAutocomplete } from "@wa/ui";
import { useT } from "../../../i18n/I18nProvider";
import { useAddressSource } from "../../../lib/address";
import { addContact, createClient, duplicateRegCode, type Client, type CrmDuplicateWarning } from "../../../lib/clients";
import { CrossWarning } from "./CrossWarning";
import { staffOptionLabel } from "../../../lib/staff";
import { Avatar, Btn, Modal, SelectInput, useToast } from "../../ui";
import { AriregisterNameInput, type ArPrefill } from "./AriregisterNameInput";
import { Field, Pill } from "./formBits";
import { useStaff } from "./staff";

export function NewClientModal({ onClose, onCreate }: { onClose: () => void; onCreate: (c: Client) => void }) {
  const t = useT();
  const addressSource = useAddressSource();
  const toast = useToast();

  const [name, setName] = useState("");
  const [kind, setKind] = useState<"company" | "person">("company");
  const [regCode, setRegCode] = useState("");
  const [vatNumber, setVat] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [website, setWebsite] = useState("");
  const [address, setAddress] = useState("");
  const [notes, setNotes] = useState("");
  const [assignedTo, setAssignedTo] = useState("");
  const [terms, setTerms] = useState("14");

  // The contact person, optional. Filled in here it becomes the client's primary
  // contact, which is what makes the client reachable at all - a company row
  // with no human on it cannot be invited to the portal or emailed.
  const [cFirst, setCFirst] = useState("");
  const [cLast, setCLast] = useState("");
  const [cRole, setCRole] = useState("");
  const [cEmail, setCEmail] = useState("");

  // The shared directory rather than an own `getStaff()`: this modal opens from
  // four places, and its own copy came back unsorted and uncached. Unsorted is
  // the part that mattered - the route returns people who have left the company
  // too, so a departed colleague sat in the middle of the alphabet looking
  // exactly like a current one. `useStaff` puts the active people first and
  // hands back the row that still carries `active`, which is what the option
  // label below needs to say so.
  const { staff } = useStaff();
  const [busy, setBusy] = useState(false);
  // The client was written AND something similar is already on the lead board.
  // Not an error and not a refusal, so it cannot be a toast: this dialog opens
  // from inside a half-written invoice, and a message that fades is a message
  // that arrives at the worst possible moment. See `CrossWarning`.
  const [saved, setSaved] = useState<{ client: Client; warnings: CrmDuplicateWarning[] } | null>(null);

  function applyPrefill(p: ArPrefill) {
    setName(p.name);
    setKind("company");
    setRegCode(p.regCode);
    setVat(p.vatNumber ?? "");
    setEmail(p.email ?? "");
    setPhone(p.phone ?? "");
    setWebsite(p.website ?? "");
    setAddress(p.address ?? "");
  }

  const contactName = [cFirst, cLast].filter(Boolean).join(" ");

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim() || busy) return;
    setBusy(true);
    try {
      const client = await createClient({
        name: name.trim(),
        kind,
        regCode: regCode || undefined,
        vatNumber: vatNumber || undefined,
        email: email || undefined,
        phone: phone || undefined,
        website: website || undefined,
        addressLine: address || undefined,
        notes: notes || undefined,
        assignedTo: assignedTo || undefined,
        paymentTermsDays: terms.trim() ? Number(terms) : undefined,
      });

      // A failed contact must not lose the client that was already created, so
      // this is a separate step with its own outcome rather than part of the
      // create call.
      if (cFirst.trim()) {
        try {
          await addContact(client.id, {
            firstName: cFirst.trim(),
            lastName: cLast.trim() || undefined,
            email: cEmail.trim() || undefined,
            position: cRole.trim() || undefined,
            isPrimary: true,
          });
        } catch {
          toast(t("clients.contactFailed"));
        }
      }

      const warnings = client.warnings ?? [];
      if (warnings.length) {
        // `onCreate` is deferred to the acknowledgement: one of the callers
        // navigates straight to the new client and would take the warning off
        // the screen before it was read.
        setSaved({ client, warnings });
        return;
      }
      onCreate(client);
      onClose();
    } catch (err) {
      // A registry code already on a client. The modal stays open with the form
      // intact - this one is opened from an invoice as often as from the client
      // list, and navigating away mid-document would lose more than it fixes.
      const dup = duplicateRegCode(err);
      toast(dup?.clientId ? t("clients.duplicateRegCode", { name: dup.name }) : t("common.error"));
    } finally {
      setBusy(false);
    }
  }

  // Saved, with an open lead carrying the same company. The form is gone: it
  // has done its work, and a second submit would file the client twice.
  if (saved) {
    const done = () => { onCreate(saved.client); onClose(); };
    return (
      <Modal title={t("clients.createdTitle")} label={t("clients.createdTitle")} onClose={done}>
        <CrossWarning warnings={saved.warnings} />
        <div className="crm-m-foot">
          <Btn type="button" tone="dark" onClick={done}>{t("common.close")}</Btn>
        </div>
      </Modal>
    );
  }

  return (
    <Modal title={t("clients.addTitle")} label={t("clients.addTitle")} onClose={onClose}>
      <form onSubmit={submit}>
        <Field label={t("clients.name")}>
          <AriregisterNameInput
            pill
            value={name}
            onChange={setName}
            onPick={applyPrefill}
            placeholder={t("clients.namePlaceholder")}
            required
            enabled={kind === "company"}
            hint={kind === "company" ? t("ariregister.autofill.hint") : undefined}
          />
        </Field>

        <Field label={t("clients.type")}>
          <SelectInput
            value={kind}
            onChange={(next) => setKind(next as "company" | "person")}
            label={t("clients.type")}
          >
            <option value="company">{t("clients.company")}</option>
            <option value="person">{t("clients.person")}</option>
          </SelectInput>
        </Field>

        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <Field label={t("clients.regCode")}>
              <Pill value={regCode} onChange={setRegCode} placeholder="12345678" inputMode="numeric" />
            </Field>
          </div>
          <div>
            <Field label={t("clients.vat")}>
              <Pill value={vatNumber} onChange={setVat} placeholder="EE123456789" />
            </Field>
          </div>
          <div>
            <Field label={t("clients.email")}>
              <Pill value={email} onChange={setEmail} placeholder={t("common.example.email")} type="email" inputMode="email" />
            </Field>
          </div>
          <div>
            <Field label={t("clients.phone")}>
              <Pill value={phone} onChange={setPhone} placeholder="+372 5xxx xxxx" inputMode="tel" />
            </Field>
          </div>
        </div>

        <Field label={t("clients.website")}>
          <Pill value={website} onChange={setWebsite} placeholder={t("common.example.website")} inputMode="url" />
        </Field>

        <Field label={t("clients.address")}>
          <AddressAutocomplete
            className="crm-inp"
            inputClassName="h-auto border-0 bg-transparent p-0 shadow-none focus-visible:ring-0"
            source={addressSource}
            value={address}
            onChange={setAddress}
            placeholder={t("clients.addressPlaceholder")}
            aria-label={t("clients.address")}
          />
        </Field>

        {/* The design shows a picked contact here as an avatar row. Ours has to
            take one, not display one - there is nobody to pick from until the
            client exists. */}
        <Field label={t("clients.contactPerson")}>
          <div className="crm-inp">
            {contactName ? <Avatar name={contactName} size={38} /> : null}
            <input
              value={cFirst}
              onChange={(e) => setCFirst(e.target.value)}
              placeholder={t("clients.firstName")}
              autoComplete="off"
            />
            <input
              value={cLast}
              onChange={(e) => setCLast(e.target.value)}
              placeholder={t("clients.lastName")}
              autoComplete="off"
            />
          </div>
        </Field>
        {cFirst.trim() ? (
          <div className="mt-2.5 grid gap-3 sm:grid-cols-2">
            <Pill value={cRole} onChange={setCRole} placeholder={t("clients.position")} />
            <Pill value={cEmail} onChange={setCEmail} placeholder={t("clients.email")} type="email" inputMode="email" />
          </div>
        ) : null}

        <Field label={t("clients.assignedTo")}>
          <SelectInput value={assignedTo} onChange={setAssignedTo} label={t("clients.assignedTo")}>
            <option value="">{t("clients.unassigned")}</option>
            {/* The word rides in the label rather than beside it, so a leaver
                reads the same way in every list that draws these options. */}
            {staff.map((s) => (
              <option key={s.id} value={s.id}>
                {staffOptionLabel(s, t("common.inactive"))}
              </option>
            ))}
          </SelectInput>
        </Field>

        {/* The design has a credit-limit slider here. There is no credit limit on
            a client; payment terms is the field that actually decides when money
            is expected, so that is what sits in this slot. */}
        <Field label={t("clients.paymentTerms")}>
          <Pill value={terms} onChange={setTerms} placeholder="14" inputMode="numeric" />
        </Field>

        <Field label={t("clients.notes")}>
          <textarea
            className="crm-area"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder={t("clients.notesPlaceholder")}
          />
        </Field>

        <div className="crm-m-foot">
          <Btn type="button" onClick={onClose} disabled={busy}>
            {t("clients.cancel")}
          </Btn>
          <Btn type="submit" tone="dark" disabled={busy || !name.trim()}>
            {busy ? <Loader2 className="size-4 animate-spin" /> : null}
            {t("clients.add")}
          </Btn>
        </div>
      </form>
    </Modal>
  );
}
